import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';
import { observer } from 'mobx-react-lite';

import { SideMenuIcons, SideMenuIconToFont } from '../../../../../enums';
import { NotificationItem } from '../../../../../store/notification-store';
import { generateSidebarIconUrl, isMobile } from '../../../../../helpers';
import { AspectRatioImage } from '../../cashback/ratio-image';
import { SidebarIcon } from '../../../../../models';
import { useStore } from '../../../../../hooks';

// each icon owns a 5s slot of the toggle cycle; keyframes variants in style.scss use the same slot length
const ICON_CYCLE_SLOT_SECONDS = 5;

// resuming the queue instantly on mount made the first notification (active promos) appear already
// opened on real mobile devices - the unpause and the first paint landed in the same frame, so the
// slide-in transition had no closed state to start from
const NOTIFICATION_RESUME_DELAY = 1_000;

interface SidebarMenuOpenerProps {
  onOpen: () => void;
  onOpenLobbyTab?: () => void;
  notifCount: number;
  icons: Array<SidebarIcon>;
  showSidebarOpener: boolean;
}

export const SidebarMenuOpener: React.FC<SidebarMenuOpenerProps> = observer(({
  icons,
  onOpen,
  onOpenLobbyTab,
  notifCount,
  showSidebarOpener
}) => {

  const {
    sidebarNotificationStore, modalStore,
    postMessageStore, gameStore, sidebarMenuStore, userActivityStore
  } = useStore();
  const { activeNotification } = sidebarNotificationStore;
  const { anyFeaturePopupOpen } = modalStore;
  const { canvasWidth } = gameStore;
  const [lastActiveNotification, setLastActiveNotification] = useState<NotificationItem<SideMenuIcons> | null>(null);
  // icons start cycling only after the active-promos announcement finished displaying
  const [promosAnnounced, setPromosAnnounced] = useState<boolean>(false);

  const showNotification = !!activeNotification;
  const isPromosNotification = lastActiveNotification?.data === SideMenuIcons.ActivePromos;
  
  // hold sidebar notifications back while any feature popup is open, resume once all are closed;
  // the resume is delayed so the opener paints closed first and the notification can slide in
  useEffect(() => {
    if (anyFeaturePopupOpen) {
      sidebarNotificationStore.setPaused(true);
      return;
    }
    const id = setTimeout(() => sidebarNotificationStore.setPaused(false), NOTIFICATION_RESUME_DELAY);
    return () => clearTimeout(id);
  }, [anyFeaturePopupOpen]);

  const activePromos = useMemo(() => {
    return icons.filter(({ type }) => type !== SideMenuIcons.Default);
  }, [icons]);

  useEffect(() => {
    if (!activeNotification) return;
    setLastActiveNotification(activeNotification);
    if (activeNotification.data === SideMenuIcons.ActivePromos) {
      // send PM to game to show sidebar active promos
      postMessageStore.sendShowSidebarPromos(activePromos);
      return;
    }
    const type = activeNotification.data || SideMenuIcons.Default;
    postMessageStore.sendShowSidebarNotification({
      type,
      iconUrl: generateSidebarIconUrl(type),
      message: activeNotification.message
    });
  }, [activeNotification]);

  // the toggle icons start cycling once the promos announcement left the screen
  // (display timed out or the player dismissed it)
  useEffect(() => {
    if (!activeNotification && lastActiveNotification?.data === SideMenuIcons.ActivePromos) {
      setPromosAnnounced(true);
    }
  }, [activeNotification, lastActiveNotification]);

  const hideNotification = useCallback(() => {
    sidebarNotificationStore.hideActiveNotification();
  }, []);

  // Clicking the notification takes the player to the detailed view of the promotion it announces
  const openPromotionDetails = useCallback((type?: SideMenuIcons) => {
    sidebarMenuStore.openPromotionDetails(type);
    hideNotification();
  }, [hideNotification]);

  const handleNotificationClick = useCallback(() => {
    // the promos row has no single target - each icon stands for its own promotion
    if (lastActiveNotification?.data === SideMenuIcons.ActivePromos) return;

    const type = lastActiveNotification?.data;

    if (isMobile() && type === SideMenuIcons.Default) {
      hideNotification();
      onOpenLobbyTab?.();
      return;
    }

    openPromotionDetails(type);
  }, [hideNotification, lastActiveNotification, onOpenLobbyTab, openPromotionDetails]);

  const handleSidebarButtonClick = useCallback(() => {
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.SidebarMenuButton
    });
    onOpen();
  }, [onOpen]);

  if (!showSidebarOpener) return null;

  const isWebWithCanvas = !isMobile() && !!canvasWidth;

  return (
    <div
      className="side-menu-new-opener-container"
      style={isWebWithCanvas ? { maxWidth: canvasWidth } : {}}
    >
      {!isMobile() && <AspectRatioImage />}
      <div className={`new-lobby-tournament ${showNotification ? 'active' : ''}`}>
        <div className="new-lobby-tournament-progress">
          <div className="new-lobby-tournament-progress-action" onClick={hideNotification}>
            <i className="icon-arrow-right" />
          </div>
          <div className="new-lobby-tournament-progress-content" onClick={handleNotificationClick}>
            {
              isPromosNotification ? (
                activePromos.map(({ type }) => (
                  <div key={type} className="icon-tournament-holder">
                    <i className={`${SideMenuIconToFont[type]} tournament-progress-icon`} />
                  </div>
                ))
              ) : (
                lastActiveNotification?.message ? (
                  <>
                    {
                      lastActiveNotification.data ? (
                        <div className="icon-tournament-holder">
                          <i className={`${SideMenuIconToFont[lastActiveNotification.data]} tournament-progress-icon`} />
                        </div>
                      ) : null
                    }
                    <p className="in-progress-text-new">{lastActiveNotification.message}</p>
                  </>
                ) : null
              )
            }
          </div>
        </div>
        <div className="new-lobby-tournament-toggle" onClick={handleSidebarButtonClick}>
          {
            icons.map(({ type }, index) => (
              <i
                key={type}
                className={`${SideMenuIconToFont[type]} lobby-toggle-icon ${type}`}
                style={icons.length > 1 && promosAnnounced ? {
                  animationName: `fancyFadeCycle-${icons.length}`,
                  animationDuration: `${icons.length * ICON_CYCLE_SLOT_SECONDS}s`,
                  animationDelay: `${index * ICON_CYCLE_SLOT_SECONDS}s`,
                } : index === 0 ? {
                  // until the promos announcement closes the icons don't cycle - keep the first one visible
                  opacity: 1,
                  transform: 'none',
                } : undefined}
              />
            ))
          }
        </div>
        {
          notifCount ? (
            <span className="sidebar-notifs" onClick={handleSidebarButtonClick}>{notifCount}</span>
          ) : null
        }
      </div>
    </div>
  );
});
